import { useEffect, useState } from "react";
import { dialLead, getCallMethod, registerWebphoneDial } from "@/lib/apiClient";
import { PhoneCall, Loader2, CheckCircle2, Globe } from "lucide-react";
import { toast } from "sonner";

const WEBPHONE_URL = "https://my.rinkel.com/dashboard?dial-number=";
let methodCache = null;

export const toE164 = (phone) => {
  let p = (phone || "").replace(/[^\d+]/g, "");
  if (p.startsWith("+")) p = p.slice(1);
  else if (p.startsWith("00")) p = p.slice(2);
  else if (p.startsWith("0")) p = "31" + p.slice(1);
  if (p.startsWith("310")) p = "31" + p.slice(3);
  return p.length >= 8 ? `+${p}` : "";
};

// Rinkel Click To Dial: the official Chrome extension intercepts clicks on tel:-links and dials in the Webphone.
// Without the extension, the same official dial-number URL opens the Webphone with the number filled in.
function WebphoneLink({ lead, label, testid, compact }) {
  const to = toE164(lead.phone);
  const register = () => registerWebphoneDial(lead.id).catch((e) => toast.error(e?.response?.data?.detail || "Gesprek kon niet aan de lead worden gekoppeld"));
  // Reaching this handler means the Click To Dial extension did NOT intercept the click (it stops the event before React sees it).
  const onClick = (e) => {
    e.preventDefault();
    toast.warning("De Rinkel Click To Dial-extensie is niet actief op deze pagina, daardoor wordt het nummer mogelijk niet ingevuld. Herlaad het CRM (F5) na het installeren en zet bij chrome://extensions → Rinkel Webphone → Details de Sitetoegang op 'Op alle sites'.", { duration: 12000 });
    window.open(WEBPHONE_URL + encodeURIComponent(to), "rinkel-webphone");
  };
  const cls = compact
    ? "inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-[#001C51] text-white hover:bg-[#0A337A]"
    : "flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-[#16BFBF] text-white font-semibold text-base hover:bg-[#12A8A8] hover:-translate-y-0.5 transition-all duration-200";
  return (
    <a data-testid={testid} href={`tel:${to}`} onMouseDown={register} onKeyDown={(e) => e.key === "Enter" && register()} onClick={onClick} className={cls}>
      {compact ? <Globe className="h-3.5 w-3.5" /> : <PhoneCall className="h-5 w-5" />} {label}
    </a>
  );
}

export function DialButton({ lead }) {
  const [method, setMethod] = useState(methodCache);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  useEffect(() => {
    if (methodCache) return;
    getCallMethod().then((m) => { methodCache = m; setMethod(m); }).catch(() => setMethod({ effective_method: "webphone", steps: [] }));
  }, []);

  if (!method) return <div className="flex items-center justify-center w-full py-3.5 rounded-xl bg-[#EDF1F5] text-[#5B6675]"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  if (method.effective_method === "none") {
    return <p data-testid="bel-dial-error" className="text-xs text-[#B0413E] rounded-xl border border-[#F3DEDD] bg-[#FDF6F6] p-3">{method.steps?.[0] || "Je CRM-account is nog niet gekoppeld aan je Rinkel-account."}</p>;
  }

  if (method.effective_method === "webphone") {
    return (
      <div className="space-y-2">
        <WebphoneLink lead={lead} label={`Bel ${lead.phone} via Rinkel`} testid="bel-call-btn" />
        <p data-testid="bel-webphone-hint" className="text-xs text-[#5B6675]">Belt via je Rinkel Webphone (Chrome, ingelogd op my.rinkel.com){method.outbound_number ? ` met ${method.outbound_number}` : ""}. Het gesprek wordt automatisch aan deze lead gekoppeld.</p>
        {!method.number_access && <p data-testid="bel-number-warning" className="text-xs text-[#B7791F]">Let op: het uitgaande nummer {method.outbound_number || ""} is niet actief bij Rinkel.</p>}
      </div>
    );
  }

  const dial = async () => {
    setState("busy"); setError("");
    try {
      const r = await dialLead(lead.id);
      setState("ok");
      toast.success(`Je Rinkel-app gaat over. Neem op om ${r.to} te bellen.`, { duration: 8000 });
      setTimeout(() => setState("idle"), 15000);
    } catch (e) {
      setState("fallback");
      setError(e?.response?.data?.detail || "Bellen via Rinkel lukte niet");
    }
  };
  return (
    <div className="space-y-2">
      <button data-testid="bel-call-btn" onClick={dial} disabled={state === "busy"}
        className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-[#16BFBF] text-white font-semibold text-base hover:bg-[#12A8A8] hover:-translate-y-0.5 disabled:opacity-60 transition-all duration-200">
        {state === "busy" ? <Loader2 className="h-5 w-5 animate-spin" /> : state === "ok" ? <CheckCircle2 className="h-5 w-5" /> : <PhoneCall className="h-5 w-5" />}
        {state === "ok" ? "Rinkel-app gaat over — neem op" : `Bel ${lead.phone} via Rinkel`}
      </button>
      {state === "ok" && <p data-testid="bel-dial-ok" className="text-xs text-[#0C6E6E]">Je Rinkel-app gaat over. Neem op; daarna verbindt Rinkel je met de lead via {method.outbound_number || "het zakelijke nummer"}.</p>}
      {state === "fallback" && (
        <div data-testid="bel-dial-fallback" className="rounded-xl border border-[#F3DEDD] bg-[#FDF6F6] p-3 space-y-2">
          <p className="text-xs text-[#B0413E]" data-testid="bel-dial-error">{error}</p>
          <WebphoneLink lead={lead} label="Bel via Rinkel Webphone" testid="bel-webphone-fallback" compact />
        </div>
      )}
    </div>
  );
}
